<template>
  <div class="resource-manager">
    <div class="page-header">
      <div>
        <h1>{{ $t('bo.resources.title') }}</h1>
        <p>{{ $t('bo.resources.subtitle') }}</p>
      </div>
      <button @click="showCreate = true" class="btn-primary">{{ $t('bo.resources.newResource') }}</button>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button :class="['tab', { active: activeTab === 'all' }]" @click="activeTab = 'all'">
        {{ $t('bo.resources.tabAll') }}
      </button>
      <button :class="['tab', { active: activeTab === 'submissions' }]" @click="activeTab = 'submissions'">
        {{ $t('bo.resources.tabPending') }}
        <span v-if="pendingCount > 0" class="badge">{{ pendingCount }}</span>
      </button>
    </div>

    <!-- Tab: All resources -->
    <div v-show="activeTab === 'all'" class="tab-content">
      <AdminResourceList
        ref="listRef"
        @edit="handleEdit"
        @publish="handlePublish"
        @archive="handleArchive"
        @review="handleReview"
      />
    </div>

    <!-- Tab: Submissions -->
    <div v-show="activeTab === 'submissions'" class="tab-content">
      <ResourceSubmissionsList ref="submissionsRef" @review="handleReview" />
    </div>

    <!-- Modals -->
    <CreateAdminResourceModal
      v-if="showCreate"
      @close="showCreate = false"
      @created="handleCreated"
    />

    <EditAdminResourceModal
      v-if="editingResource"
      :resource="editingResource"
      @close="editingResource = null"
      @updated="handleUpdated"
    />

    <ReviewResourceModal
      v-if="reviewingId"
      :resource-id="reviewingId"
      @close="reviewingId = null"
      @reviewed="handleReviewed"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AdminResourceList from './AdminResourceList.vue'
import ResourceSubmissionsList from './ResourceSubmissionsList.vue'
import CreateAdminResourceModal from './CreateAdminResourceModal.vue'
import EditAdminResourceModal from './EditAdminResourceModal.vue'
import ReviewResourceModal from './ReviewResourceModal.vue'
import adminResourceService from '@/services/adminResourceService'

const { t } = useI18n()

const activeTab = ref('all')
const showCreate = ref(false)
const editingResource = ref(null)
const reviewingId = ref(null)
const pendingCount = ref(0)
const listRef = ref(null)
const submissionsRef = ref(null)

const loadPendingCount = async () => {
  try {
    const res = await adminResourceService.getSubmissions({ per_page: 1 })
    pendingCount.value = res.data?.data?.total || 0
  } catch {
    // silent
  }
}

const handleEdit = (resource) => {
  editingResource.value = resource
}

const handlePublish = async (id) => {
  if (!confirm(t('bo.resources.confirmPublish'))) return
  try {
    await adminResourceService.publish(id)
    listRef.value?.loadResources()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.resources.failedPublish'))
  }
}

const handleArchive = async (id) => {
  if (!confirm(t('bo.resources.confirmArchive'))) return
  try {
    await adminResourceService.archive(id)
    listRef.value?.loadResources()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.resources.failedArchive'))
  }
}

const handleReview = (id) => {
  reviewingId.value = id
}

const handleCreated = () => {
  showCreate.value = false
  listRef.value?.loadResources()
}

const handleUpdated = () => {
  editingResource.value = null
  listRef.value?.loadResources()
}

const handleReviewed = () => {
  reviewingId.value = null
  submissionsRef.value?.loadSubmissions()
  loadPendingCount()
}

onMounted(loadPendingCount)
</script>

<style scoped>
.resource-manager {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.page-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem;
}

.page-header p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
}

.btn-primary:hover { background: #2563eb; }

.tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.tab {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.15s;
}

.tab:hover { color: #374151; }

.tab.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  font-weight: 600;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: white;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
}

.tab-content {
  min-height: 200px;
}
</style>
